import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@core/auth/hooks/useAuth';
import { getModuleByPath, getModuleInfoList } from './moduleRegistry';
import type { ModuleInfo } from './types';

interface AccessibleModules {
  /** Modules the user can open, in selector order */
  accessibleModules: ModuleInfo[];
  /** Module of the current URL, or the first accessible module on non-module pages */
  currentModule: ModuleInfo | null;
}

/**
 * Modules available to the current user.
 *
 * Any permission with the module's prefix grants access
 * (e.g. any "Fleet." permission opens the fleet module).
 */
export const useAccessibleModules = (): AccessibleModules => {
  const { permissions } = useAuth();
  const location = useLocation();

  const accessibleModules = useMemo(
    () =>
      getModuleInfoList().filter((module) => {
        if (!module.accessPermission) return true;
        const modulePrefix = module.accessPermission.split('.')[0] + '.';
        return permissions.some((p) => p.startsWith(modulePrefix));
      }),
    [permissions]
  );

  const currentModule = useMemo(
    () => getModuleByPath(location.pathname)?.moduleInfo ?? accessibleModules[0] ?? null,
    [location.pathname, accessibleModules]
  );

  return { accessibleModules, currentModule };
};
